/**
 * Status model and review transitions (spec section 3.1).
 *
 * The transition table is data, and the API's review RPC consults it before
 * touching the database, so a client cannot invent a transition. Member
 * reporters never change status at all; that is enforced by role, not here.
 */

export const DAMAGE_STATUSES = [
  'gemeldet',
  'bekannt',
  'beobachtung',
  'in_reparatur',
  'akzeptiert',
  'behoben',
  'verworfen',
] as const;
export type DamageStatus = (typeof DAMAGE_STATUSES)[number];

/**
 * Statuses a damage can only reach through the review, never at its creation:
 * both need a reason, which the create has no field for. The API refuses them
 * (decideCreateStatus) and every form leaves them out — the app offered
 * „Akzeptiert“ and its report then failed in the outbox (bug hunt 15.10.2026).
 */
export const REVIEW_ONLY_STATUSES: readonly DamageStatus[] = ['verworfen', 'akzeptiert'];

export const DAMAGE_CATEGORIES = ['lack', 'kratzer', 'beule', 'riss', 'sonstiges'] as const;
export type DamageCategory = (typeof DAMAGE_CATEGORIES)[number];

export const MEMBER_ROLES = ['member', 'editor', 'admin', 'inactive'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export type ReviewAction =
  'confirm' | 'observe' | 'repair' | 'accept' | 'resolve' | 'reject' | 'merge' | 'reopen';

/** Which actions are allowed from which status. Keys absent = no way out. */
const TRANSITIONS: Readonly<Record<DamageStatus, readonly ReviewAction[]>> = {
  gemeldet: ['confirm', 'observe', 'repair', 'accept', 'reject', 'merge'],
  bekannt: ['observe', 'repair', 'accept', 'resolve', 'reject'],
  beobachtung: ['confirm', 'repair', 'accept', 'resolve', 'reject'],
  in_reparatur: ['resolve', 'observe', 'accept', 'confirm'],
  // Accepted damage stays as it is on purpose; it can still be repaired after all, or reopened.
  akzeptiert: ['repair', 'resolve', 'reopen'],
  behoben: ['reopen'],
  verworfen: ['reopen'],
};

const TARGET: Readonly<Record<ReviewAction, DamageStatus>> = {
  confirm: 'bekannt',
  observe: 'beobachtung',
  repair: 'in_reparatur',
  accept: 'akzeptiert',
  resolve: 'behoben',
  reject: 'verworfen',
  merge: 'verworfen',
  reopen: 'bekannt',
};

export function isDamageStatus(value: unknown): value is DamageStatus {
  return typeof value === 'string' && (DAMAGE_STATUSES as readonly string[]).includes(value);
}

export function isDamageCategory(value: unknown): value is DamageCategory {
  return typeof value === 'string' && (DAMAGE_CATEGORIES as readonly string[]).includes(value);
}

export function isMemberRole(value: unknown): value is MemberRole {
  return typeof value === 'string' && (MEMBER_ROLES as readonly string[]).includes(value);
}

export function allowedActions(from: DamageStatus): readonly ReviewAction[] {
  return TRANSITIONS[from];
}

export function canTransition(from: DamageStatus, action: ReviewAction): boolean {
  return TRANSITIONS[from].includes(action);
}

export function targetStatus(action: ReviewAction): DamageStatus {
  return TARGET[action];
}

/**
 * Reject, merge and accept need a reason the reporter can read (DB CHECK).
 * Owner, 08.10.2026: one character is enough — the reason must be there, not long.
 */
export const REVIEW_NOTE_MIN_LENGTH = 1;

/** Reject, merge and accept need a reason the reporter and every pilot will read (owner, 08.10.2026 for accept). */
export function actionNeedsReason(action: ReviewAction): boolean {
  return action === 'reject' || action === 'merge' || action === 'accept';
}

/** Roles ordered by power, for "at least editor" checks. */
const ROLE_RANK: Readonly<Record<MemberRole, number>> = {
  inactive: 0,
  member: 1,
  editor: 2,
  admin: 3,
};

export function hasAtLeastRole(
  role: MemberRole,
  required: Exclude<MemberRole, 'inactive'>,
): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[required];
}

/**
 * Who may delete a Rückfrage/Hinweis: its author, or Technik and Verwaltung.
 * One rule for the API (which decides) and for web and app (which only show
 * the button where the API will agree). Ids compare case-insensitively.
 */
export function mayDeleteComment(role: MemberRole, actorId: string, authorId: string): boolean {
  return actorId.toLowerCase() === authorId.toLowerCase() || hasAtLeastRole(role, 'editor');
}

/**
 * Statuses that count as "open": the fleet overview, and what the plan's
 * filter starts with. Owner, 07.10.2026: the fleet matches the plan,
 * „Gemeldet“ and „Bekannt“ — in Beobachtung and in Reparatur are known and
 * handled, and counting them made a card say more than the plan showed.
 */
export const OPEN_STATUSES: readonly DamageStatus[] = ['gemeldet', 'bekannt'];

/** Statuses the nearby search shows. 'verworfen' is hidden; 'behoben' stays visible but subdued. */
export const NEARBY_VISIBLE_STATUSES: readonly DamageStatus[] = [
  'gemeldet',
  'bekannt',
  'beobachtung',
  'in_reparatur',
  'akzeptiert',
  'behoben',
];
